import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { useActiveProfile } from './ActiveProfileContext';

const WebSocketContext = createContext();

export const useWebSocketContext = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const { activeProfile } = useActiveProfile() || {};
  // Детские профили не имеют доступа к чату (постановка «Родители и дети») —
  // не подключаемся к чат-сокету, чтобы не получать чужие для профиля сообщения.
  const isChildMode = Boolean(activeProfile?.is_child);

  const hasJWT = () => !!localStorage.getItem('token');
  const shouldConnect = hasJWT() && !isChildMode;

  const getSocketUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !process.env.REACT_APP_CHAT_WS_URL || isChildMode) return null;

    return `${process.env.REACT_APP_CHAT_WS_URL}/v1/connect?token=${encodeURIComponent(token)}`;
  }, [isChildMode]);

  const socket = useWebSocket(getSocketUrl, {
    shouldReconnect: () => shouldConnect,
    reconnectInterval: 3000,
    reconnectAttempts: 20,
    retryOnError: true,
  }, shouldConnect);

  // При входе в детский профиль уже открытое соединение закрываем сразу —
  // shouldReconnect не даст переподключиться, пока isChildMode активен.
  useEffect(() => {
    if (isChildMode) {
      socket.getWebSocket()?.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChildMode]);

  const sendJsonMessageRef = useRef(socket.sendJsonMessage);

  useEffect(() => {
    sendJsonMessageRef.current = socket.sendJsonMessage;
  }, [socket.sendJsonMessage]);

  const sendJsonMessage = useCallback(
    (message) => sendJsonMessageRef.current(message, false),
    []
  );

  const value = useMemo(
    () => ({
      ...socket,
      sendJsonMessage,
    }),
    [socket, sendJsonMessage]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
